import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowDown, ArrowUp, GripVertical, Lock, Plus, Trash2 } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { type Stufe, PHASEN_FARBEN, crmFetch } from "@/lib/crm"

/**
 * Phasen bearbeiten (Paul 26.10.2026: „wie kann ich die phasennamen ändern
 * und löschen und neue hinzufügen?").
 *
 * Alles wird lokal bearbeitet und erst mit „Speichern" in einem Rutsch
 * geschrieben — ein halb umgebautes Board zwischen zwei Klicks gäbe es sonst.
 *
 * 🔑 „Gewonnen" und „Verloren" bleiben (umbenennbar, nicht löschbar): an ihnen
 * hängen Abschlussquote und Erinnerungen. Wer eine Phase mit Deals löscht,
 * sagt, wohin die Deals wandern.
 */

type Zeile = Stufe & { neu?: boolean; schluesselNeu?: string }

export function PhasenEditor({ zu }: { zu: () => void }) {
  const qc = useQueryClient()
  const q = useQuery<{ stufen: Stufe[]; deals: Record<string, number> }>({
    queryKey: ["crm-stufen"],
    queryFn: () => crmFetch("/stufen"),
  })
  const [zeilen, setZeilen] = useState<Zeile[] | null>(null)
  const [geloescht, setGeloescht] = useState<Stufe[]>([])
  const [ziele, setZiele] = useState<Record<string, string>>({})
  const [farbeOffen, setFarbeOffen] = useState<number | null>(null)
  const [zieht, setZieht] = useState<number | null>(null)

  useEffect(() => { if (q.data && !zeilen) setZeilen(q.data.stufen) }, [q.data, zeilen])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && !(e.target as HTMLElement)?.matches?.("input")) zu() }
    window.addEventListener("keydown", esc)
    return () => window.removeEventListener("keydown", esc)
  }, [zu])

  const deals = q.data?.deals || {}
  const speichern = useMutation({
    mutationFn: () => crmFetch("/stufen", {
      method: "PUT",
      json: {
        stufen: (zeilen || []).map((z) => ({
          schluessel: z.neu ? null : z.schluessel, label: z.label.trim(), art: z.art, farbe: z.farbe, tage: z.tage || null,
        })),
        verschieben: Object.fromEntries(geloescht.filter((s) => deals[s.schluessel]).map((s) => [s.schluessel, ziele[s.schluessel]])),
      },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-board"] })
      qc.invalidateQueries({ queryKey: ["crm-stufen"] })
      qc.invalidateQueries({ queryKey: ["crm-faellig"] })
      toast("Phasen gespeichert")
      zu()
    },
    onError: (e: Error) => toast(e.message, "error"),
  })

  if (!zeilen) {
    return createPortal(<div className="vt-portal"><div className="vt-schleier" onClick={zu} /></div>, document.body)
  }

  const setze = (i: number, teil: Partial<Zeile>) => setZeilen(zeilen.map((z, j) => (j === i ? { ...z, ...teil } : z)))
  const schiebe = (von: number, nach: number) => {
    if (nach < 0 || nach >= zeilen.length || von === nach) return
    const neu = [...zeilen]
    const [z] = neu.splice(von, 1)
    neu.splice(nach, 0, z)
    setZeilen(neu)
  }
  const loesche = (i: number) => {
    const z = zeilen[i]
    if (!z.neu) setGeloescht([...geloescht, z])
    setZeilen(zeilen.filter((_, j) => j !== i))
  }
  const hinzu = () => {
    // Neue Phase vor „Gewonnen" — dort gehört eine offene Phase hin.
    const idx = zeilen.findIndex((z) => z.art !== "offen")
    const pos = idx === -1 ? zeilen.length : idx
    const neu: Zeile = {
      schluessel: `neu-${Date.now()}`, neu: true, label: "", position: pos, art: "offen",
      farbe: PHASEN_FARBEN[(zeilen.length * 3) % PHASEN_FARBEN.length], tage: 3,
    }
    const liste = [...zeilen]
    liste.splice(pos, 0, neu)
    setZeilen(liste)
  }

  const bleibende = zeilen.filter((z) => !z.neu)
  const fehlendesZiel = geloescht.some((s) => deals[s.schluessel] && !ziele[s.schluessel])
  const leererName = zeilen.some((z) => !z.label.trim())

  return createPortal(
    <div className="vt-portal">
      <div className="vt-schleier" onClick={zu} />
      <div className="vt-dialog vt-phasen" role="dialog" aria-label="Phasen bearbeiten">
        <div className="vt-dialog-kopf">
          <h3>Phasen bearbeiten</h3>
          <p>Umbenennen, verschieben, Farbe wählen – und nach wie vielen ruhigen Tagen der Reiter erinnert.</p>
        </div>
        <div className="vt-dialog-rumpf">
          <ul className="vt-phasen-liste">
            {zeilen.map((z, i) => {
              const fest = z.art !== "offen"
              return (
                <li key={z.schluessel}
                  className={`vt-phase ${zieht === i ? "zieht" : ""}`}
                  draggable
                  onDragStart={(e) => { setZieht(i); e.dataTransfer.effectAllowed = "move" }}
                  onDragOver={(e) => { e.preventDefault(); if (zieht !== null && zieht !== i) { schiebe(zieht, i); setZieht(i) } }}
                  onDragEnd={() => setZieht(null)}
                >
                  <span className="vt-phase-griff" title="Ziehen zum Sortieren"><GripVertical /></span>
                  <div className="vt-phase-farbe">
                    <button type="button" className="vt-phase-punkt" style={{ background: z.farbe || "#94a3b8" }}
                      title="Farbe" onClick={() => setFarbeOffen(farbeOffen === i ? null : i)} />
                    {farbeOffen === i && (
                      <div className="vt-farben">
                        {PHASEN_FARBEN.map((f) => (
                          <button key={f} type="button" style={{ background: f }} className={f === z.farbe ? "an" : ""}
                            onClick={() => { setze(i, { farbe: f }); setFarbeOffen(null) }} />
                        ))}
                      </div>
                    )}
                  </div>
                  <input className="vt-eingabe vt-phase-name" value={z.label} placeholder="Name der Phase"
                    autoFocus={z.neu && !z.label} onChange={(e) => setze(i, { label: e.target.value })} />
                  {fest ? (
                    <span className="vt-pille" title="Bleibt immer – hängt an Abschlussquote und Erinnerungen">
                      <Lock size={11} /> {z.art === "gewonnen" ? "Gewonnen" : "Verloren"}
                    </span>
                  ) : (
                    <label className="vt-phase-tage" title="Erinnern, wenn so viele Tage nichts passiert">
                      nach
                      <input className="vt-eingabe" type="number" min={1} max={365} value={z.tage ?? ""}
                        onChange={(e) => setze(i, { tage: e.target.value ? Number(e.target.value) : null })} placeholder="–" />
                      T
                    </label>
                  )}
                  <span className="vt-phase-anzahl" title="Deals in dieser Phase">{z.neu ? "neu" : (deals[z.schluessel] || 0)}</span>
                  <div className="vt-phase-knoepfe">
                    <button type="button" className="vt-ikon" title="Nach oben" disabled={i === 0} onClick={() => schiebe(i, i - 1)}><ArrowUp /></button>
                    <button type="button" className="vt-ikon" title="Nach unten" disabled={i === zeilen.length - 1} onClick={() => schiebe(i, i + 1)}><ArrowDown /></button>
                    <button type="button" className="vt-ikon" title={fest ? "Diese Phase bleibt" : "Löschen"} disabled={fest} onClick={() => loesche(i)}><Trash2 /></button>
                  </div>
                </li>
              )
            })}
          </ul>
          <button type="button" className="vt-spalte-neu" style={{ marginTop: 6 }} onClick={hinzu}><Plus /> Phase hinzufügen</button>

          {geloescht.filter((s) => deals[s.schluessel]).map((s) => (
            <div key={s.schluessel} className="vt-warnung" style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ flex: 1, minWidth: 200 }}>„{s.label}“ wird gelöscht – {deals[s.schluessel]} {deals[s.schluessel] === 1 ? "Deal wandert" : "Deals wandern"} nach</span>
              <select className="vt-eingabe" style={{ width: 220 }} value={ziele[s.schluessel] || ""}
                onChange={(e) => setZiele({ ...ziele, [s.schluessel]: e.target.value })}>
                <option value="">Phase wählen …</option>
                {bleibende.map((b) => <option key={b.schluessel} value={b.schluessel}>{b.label || "(ohne Namen)"}</option>)}
              </select>
              <button type="button" className="vt-knopf leise klein" onClick={() => {
                setGeloescht(geloescht.filter((g) => g.schluessel !== s.schluessel))
                setZeilen([...zeilen, s].sort((a, b) => a.position - b.position))
              }}>Rückgängig</button>
            </div>
          ))}
        </div>
        <div className="vt-dialog-fuss">
          <span className="vt-hinweis" style={{ marginRight: "auto", alignSelf: "center" }}>
            {leererName ? "Jede Phase braucht einen Namen." : fehlendesZiel ? "Bitte Ziel für die Deals wählen." : ""}
          </span>
          <button type="button" className="vt-knopf leise" onClick={zu}>Abbrechen</button>
          <button type="button" className="vt-knopf haupt" disabled={leererName || fehlendesZiel || speichern.isPending}
            onClick={() => speichern.mutate()}>{speichern.isPending ? "Speichert …" : "Speichern"}</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
